import { useMemo, useState } from 'react';
import { api, marketEvents as Api, type EventImpact, type MarketEvent, type MarketEventInput, type PubState } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim } from '../components/Shimmer';
import { useSelection, TickAll, Tick, BulkActions, type Selection } from '../components/Bulk';
import { Modal } from '../components/Modal';
import { Field, Row, StateTag } from '../components/Field';

const IMPACTS: EventImpact[] = ['high', 'medium', 'low'];
const IMPACT_COLOUR: Record<EventImpact, string> = {
  high: 'rgba(240,100,91,.13)', medium: 'rgba(var(--goldrgb), .14)', low: 'rgba(var(--wash), calc(.06 * var(--washx)))',
};

const day = (v: string) => {
  const d = new Date(`${v}T00:00:00Z`);
  return isNaN(d.valueOf()) ? v
    : d.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'UTC' });
};
const today = () => new Date().toISOString().slice(0, 10);

export function MarketCalendar() {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [editing, setEditing] = useState<MarketEvent | 'new' | null>(null);
  const [syncing, setSyncing] = useState(false);

  async function syncEvents() {
    setSyncing(true);
    try {
      await api.post('/market-events/sync', {});
      await reload();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setSyncing(false);
    }
  }

  const { upcoming, past } = useMemo(() => {
    const all = [...(data?.items ?? [])].sort((a, b) => a.event_date.localeCompare(b.event_date));
    const t = today();
    return {
      upcoming: all.filter((e) => e.event_date >= t),
      past: all.filter((e) => e.event_date < t).reverse(),
    };
  }, [data]);

  /* One selection across both tables: an id is an id whether the event is
     ahead or behind, and two independent bars would be two counts to reconcile. */
  const sel = useSelection([...upcoming, ...past]);

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Market calendar</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            {loading && !data ? <Shim w={210} h={12} style={{ marginTop: 3 }} />
              : upcoming.length ? `${upcoming.length} ahead` : 'Nothing scheduled ahead.'}
            {' · '}shown on the member area’s forex screen, not the public site
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="b bo" type="button" onClick={syncEvents} disabled={syncing}>
            {syncing ? 'Syncing…' : '⚡ Auto-Sync Events'}
          </button>
          <button className="b bg" type="button" onClick={() => setEditing('new')}>New event</button>
        </div>
      </div>

      {error ? <div className="err">Could not load the calendar — {error.message}</div> : null}

      <BulkActions
        path="/market-events" noun="event"
        rows={sel.picked.map((e) => ({ id: e.id, label: String(e.title) }))}
        actions={[['publish', 'Publish'], ['unpublish', 'Unpublish']]}
        onDone={async () => { sel.clear(); await reload(); }}
      />

      <EventTable title="Ahead" rows={upcoming} onEdit={setEditing} sel={sel}
        empty="Nothing scheduled. Only events dated today or later reach the member area." />
      {past.length ? <EventTable title="Past" rows={past} onEdit={setEditing} sel={sel} empty="" /> : null}

      {editing ? (
        <EventForm
          event={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      ) : null}
    </div>
  );
}

function EventTable({ title, rows, empty, onEdit, sel }: {
  title: string;
  rows: MarketEvent[];
  empty: string;
  onEdit: (e: MarketEvent) => void;
  sel: Selection;
}) {
  return (
    <div className="crd" style={{ padding: '20px 22px', marginBottom: 16 }}>
      <div className="kh" style={{ marginBottom: 12 }}>{title}</div>
      {rows.length === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>{empty}</p>
      ) : (
        <div className="tblx">
          <table className="tbl">
            <thead>
              <tr>
                <TickAll on={rows.every((e) => sel.isOn(e.id))} some={rows.some((e) => sel.isOn(e.id))}
                  onChange={(v) => sel.toggleMany(rows.map((e) => e.id), v)} /><th>Date</th><th>Time</th><th>Event</th><th>Impact</th><th>State</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} style={sel.isOn(e.id) ? { background: 'rgba(var(--goldrgb), .05)' } : undefined}>
                  <Tick on={sel.isOn(e.id)} label={String(e.title)}
                    onChange={(v) => sel.toggle(e.id, v)} />
                  <td style={{ whiteSpace: 'nowrap', fontWeight: 700 }}>{day(e.event_date)}</td>
                  <td className="muted">{e.at_time || '—'}</td>
                  <td>{e.title}
                    {e.note ? <div style={{ fontSize: 11.5, color: 'var(--tx3)' }}>{e.note}</div> : null}</td>
                  <td><span className="tg" style={{ background: IMPACT_COLOUR[e.impact] }}>{e.impact}</span></td>
                  <td><StateTag state={e.state} /></td>
                  <td className="right">
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                      type="button" onClick={() => onEdit(e)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EventForm({ event, onCancel, onSaved }: {
  event: MarketEvent | null;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [f, setF] = useState<MarketEventInput>(() => event ? { ...event } : {
    event_date: today(), at_time: '', title: '', note: '', impact: 'medium', state: 'published', sort: 0,
  });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const set = <K extends keyof MarketEventInput>(k: K, v: MarketEventInput[K]) => setF((p) => ({ ...p, [k]: v }));

  async function submit(state: PubState) {
    if (!f.title.trim()) { setError('An event name is required.'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f.event_date)) { setError('Give the date as YYYY-MM-DD.'); return; }
    setError('');
    setStatus('Saving…');
    try {
      if (event) await Api.update(event.id, { ...f, state });
      else await Api.create({ ...f, state });
      await onSaved();
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  async function remove() {
    if (!event || !confirm(`Delete “${event.title}”?`)) return;
    try { await Api.remove(event.id); await onSaved(); }
    catch (e) { setError((e as Error).message); }
  }

  return (
    <Modal
      title={event ? `Edit event · ${event.title}` : 'New market event'}
      onClose={onCancel}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
          {event ? (
            <button className="b bo" type="button" style={{ marginRight: 'auto' }} onClick={() => void remove()}>Delete</button>
          ) : null}
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel}>Cancel</button>
          <button className="b bo" type="button" onClick={() => void submit('draft')}>Save draft</button>
          <button className="b bg" type="button" onClick={() => void submit('published')}>Publish</button>
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}

      <Row>
        <Field label="Date"><input className="inp" type="date" value={f.event_date}
          onChange={(e) => set('event_date', e.target.value)} /></Field>
        <Field label="Time"><input className="inp" value={f.at_time} placeholder="18:00 IST"
          onChange={(e) => set('at_time', e.target.value)} /></Field>
        <Field label="Impact">
          <select className="inp" value={f.impact} onChange={(e) => set('impact', e.target.value as EventImpact)}>
            {IMPACTS.map((i) => <option key={i}>{i}</option>)}
          </select></Field>
      </Row>

      <Field label="Event"><input className="inp" value={f.title} autoFocus placeholder="RBI policy statement"
        onChange={(e) => set('title', e.target.value)} /></Field>
      <Field label="Note"><input className="inp" value={f.note}
        onChange={(e) => set('note', e.target.value)} /></Field>
    </Modal>
  );
}
